import { TestGraph } from "../types";
import {
    ANAEROBIC_THRESHOLD_CANCEL_EDITS,
    ANAEROBIC_THRESHOLD_CONFIRM_EDITS,
    ANAEROBIC_THRESHOLD_GRAPHS_ENDPOINT,
    ANAEROBIC_THRESHOLD_GRAPHS_RECALCULATE_ENDPOINT,
    ANAEROBIC_THRESHOLD_TABLES_ENDPOINT,
} from "./endpoints";
import { Nullable, VATValues, VATValuesFromAPI } from "./types";
import { fetcher } from "./utils";

export type AnaerobicThresholdValuesUpdate = Pick<
    TestGraph,
    | "vat_value"
    | "rcp_value"
    | "slope_calculation_begin_value"
    | "slope_calculation_end_value"
> & {
    testId?: string;
    graph_id?: string;
};

export const getAnaerobicThresholdGraphs = async ({
    testId,
    graphId,
    compare = false,
}: {
    testId: Nullable<string>;
    graphId?: string;
    compare?: boolean;
}) => {
    try {
        if (!testId) {
            return;
        }
        let params = { test_id: testId } as Record<string, any>;
        if (graphId) {
            params = { ...params, graph_id: graphId };
        }
        if (compare && graphId) {
            params = { ...params, compare };
        }

        const response = await fetcher(ANAEROBIC_THRESHOLD_GRAPHS_ENDPOINT, {
            params,
        });

        if (!response) return null;

        return response as TestGraph[];
    } catch (e) {
        console.error(e);
        return;
    }
};

export const getAnaerobicThresholdTablesData = async ({
    testId,
}: {
    testId: Nullable<string>;
}): Promise<VATValues | null> => {
    try {
        if (!testId) {
            return null;
        }

        const response = (await fetcher(ANAEROBIC_THRESHOLD_TABLES_ENDPOINT, {
            params: {
                test_id: testId,
            },
        })) as VATValuesFromAPI | null;

        if (!response) return null;

        const vatValues = {
            hr: response.hr?.vat,
            rer: response.rer?.vat,
            vat_percent_predicted_vo2_max:
                response.vat_percent_predicted_vo2_max?.vat,
            vat_percent_vo2_max: response.vat_percent_vo2_max?.vat,
            vo2: response.vo2?.vat,
            vo2_kg: response.vo2_kg?.vat,
            wr: response.wr?.vat,
            ve_vco2: response.ve_vco2?.vat,
        };
        const rcpValues = {
            hr: response.hr?.rcp,
            rer: response.rer?.rcp,
            vat_percent_predicted_vo2_max:
                response.vat_percent_predicted_vo2_max?.rcp,
            vat_percent_vo2_max: response.vat_percent_vo2_max?.rcp,
            vo2: response.vo2?.rcp,
            vo2_kg: response.vo2_kg?.rcp,
            wr: response.wr?.rcp,
        };
        const sortValues = (data: Object) => {
            // sort the keys by response keys order
            return Object.fromEntries(
                Object.entries(data).sort(
                    ([aKey], [bKey]) =>
                        Object.keys(response).indexOf(aKey) -
                        Object.keys(response).indexOf(bKey)
                )
            );
        };

        return {
            vat: sortValues(vatValues),
            rcp: sortValues(rcpValues),
            labels: {
                ve_vco2: [
                    response.ve_vco2?.label ?? "",
                    response.ve_vco2?.rcp_label ?? "",
                ],
                hr: [response.hr?.label ?? "", response.hr?.rcp_label ?? ""],
                rer: [response.rer?.label ?? "", response.rer?.rcp_label ?? ""],
                vat_percent_predicted_vo2_max: [
                    response.vat_percent_predicted_vo2_max?.label ?? "",
                    response.vat_percent_predicted_vo2_max?.rcp_label ?? "",
                ],
                vat_percent_vo2_max: [
                    response.vat_percent_vo2_max?.label ?? "",
                    response.vat_percent_vo2_max?.rcp_label ?? "",
                ],
                vo2: [response.vo2?.label ?? "", response.vo2?.rcp_label ?? ""],
                vo2_kg: [
                    response.vo2_kg?.label ?? "",
                    response.vo2_kg?.rcp_label ?? "",
                ],
                wr: [response.wr?.label ?? "", response.wr?.rcp_label ?? ""],
            },
        };
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const updateAnaerobicThresholdValues = async ({
    testId,
    graph_id,
    ...data
}: AnaerobicThresholdValuesUpdate) => {
    try {
        if (!testId || !graph_id) {
            throw new Error("Test ID and Graph ID is required");
        }
        const resData = await fetcher(ANAEROBIC_THRESHOLD_GRAPHS_ENDPOINT, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            params: {
                test_id: testId,
                graph_id,
            },
            body: JSON.stringify(data),
        });

        return resData as TestGraph;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const recalculateAnaerobicGraphs = async ({
    testId,
}: {
    testId: string;
}): Promise<Awaited<TestGraph[] | null>> => {
    try {
        const data = await fetcher(
            ANAEROBIC_THRESHOLD_GRAPHS_RECALCULATE_ENDPOINT,
            {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                },
                params: {
                    test_id: testId,
                },
            }
        );

        return data;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const confirmAnaerobicEdits = async (
    testId: string
): Promise<void | null> => {
    try {
        const data = await fetcher(ANAEROBIC_THRESHOLD_CONFIRM_EDITS, {
            headers: {
                "Content-Type": "application/json",
            },
            params: {
                test_id: testId,
            },
        });

        return data;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const cancelAnaerobicEdits = async (
    testId: string
): Promise<void | null> => {
    try {
        const data = await fetcher(ANAEROBIC_THRESHOLD_CANCEL_EDITS, {
            headers: {
                "Content-Type": "application/json",
            },
            params: {
                test_id: testId,
            },
        });

        return data;
    } catch (e) {
        console.error(e);
        return null;
    }
};
